/**
 * useInboxWaitingStream — SSE hook for inbox agent popups.
 *
 * Opens an EventSource to /inbox/waiting/stream/ and dispatches:
 *   - waiting_chat  → a customer is waiting, claimable by any online agent
 *   - chat_assigned → a chat was auto-assigned to THIS agent
 *
 * Mirrors usePresenceStream: JWT lives in an HttpOnly cookie, so the stream is
 * opened with `withCredentials: true` (no token in the URL). On a permanent
 * close it schedules a single 5s reconnect.
 */
import { useEffect, useRef } from 'react';

// Resolve the API base the SAME way the axios client does (src/api/api.js):
// REACT_APP_API_URL when set, else a same-origin relative "/api". Never a
// hardcoded localhost — in a prod build that would point EventSource at the
// viewer's own machine. (REACT_APP_API_BASE_URL kept first for back-compat.)
const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || '/api';

export const useInboxWaitingStream = (enabled, callbacks = {}) => {
  const esRef = useRef(null);
  const callbacksRef = useRef(callbacks);
  const reconnectTimerRef = useRef(null);

  useEffect(() => {
    callbacksRef.current = callbacks;
  });

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;

    const open = () => {
      if (cancelled) return;
      const url = `${BASE_URL}/inbox/waiting/stream/`;
      // withCredentials sends the HttpOnly JWT cookie so no token in the URL.
      const es = new EventSource(url, { withCredentials: true });
      esRef.current = es;

      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const cb = callbacksRef.current;
          switch (data.type) {
            case 'connected':
              cb.onConnected?.(data);
              break;
            case 'waiting_chat':
              cb.onWaitingChat?.(data);
              break;
            case 'chat_assigned':
              cb.onChatAssigned?.(data);
              break;
            default:
              // Other modules' agent events on the same stream (e.g. voice_handoff from the Voice Agent Desk).
              cb.onOtherEvent?.(data);
              break;
          }
        } catch (e) {
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.error('[Inbox SSE] parse error:', e);
          }
        }
      };

      es.onerror = () => {
        // EventSource auto-reconnects on transient errors. On an outright
        // server rejection (readyState CLOSED) schedule a single retry so an
        // expired/rotated cookie doesn't leave us silently disconnected.
        if (es.readyState === 2 && !cancelled) {
          try { es.close(); } catch { /* noop */ }
          esRef.current = null;
          if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = setTimeout(() => {
            reconnectTimerRef.current = null;
            open();
          }, 5000);
        }
      };
    };

    open();

    return () => {
      cancelled = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (esRef.current) {
        try { esRef.current.close(); } catch { /* noop */ }
        esRef.current = null;
      }
    };
  }, [enabled]);
};

export default useInboxWaitingStream;
